import { AiUsageIndicator } from "@/components/ai-usage/AiUsageIndicator";
import { UserMenu } from "@/components/auth/user-menu";
import { reconciliationMessages } from "@/services/messages";
import { useIsAdmin } from "@/stores/auth-store";
import { useActiveTab, useTabActions } from "@/stores/tab-store";
import {
	BookOpenText,
	BookType,
	ChevronDown,
	CircleHelp,
	ClipboardCheck,
	FolderOpen,
	GitCompare,
	ScrollText,
	Settings,
	Users,
} from "lucide-react";
import { useState } from "react";

function ItemButton({
	label,
	active,
	onClick,
	icon,
}: {
	label: string;
	active: boolean;
	onClick: () => void;
	icon: React.ReactNode;
}) {
	return (
		<button
			type="button"
			onClick={onClick}
			className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
				active
					? "bg-blue-50 text-blue-700"
					: "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
			}`}
		>
			{icon}
			{label}
		</button>
	);
}

export function Sidebar() {
	const activeTab = useActiveTab();
	const isAdmin = useIsAdmin();
	const {
		openUploadTab,
		openRatesTab,
		openVerificationTab,
		openSynonymsTab,
		openActivityLogTab,
		openUsersTab,
		openAiSettingsTab,
		openHelpTab,
		openReconciliationListTab,
	} = useTabActions();

	const settingsChildActive =
		activeTab?.type === "synonyms" ||
		activeTab?.type === "activity-log" ||
		activeTab?.type === "users" ||
		activeTab?.type === "ai-settings";
	const [settingsOpen, setSettingsOpen] = useState(settingsChildActive);

	return (
		<>
			<aside className="hidden w-64 shrink-0 border-r border-slate-200 bg-white p-4 md:flex md:h-screen md:flex-col md:gap-6 md:overflow-y-auto">
				<div>
					<p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
						Efes Checkup
					</p>
					<h2 className="mt-1 text-lg font-bold text-slate-900">Рабочее место</h2>
				</div>

				<div className="space-y-1">
					<p className="px-1 text-xs font-medium uppercase tracking-wide text-slate-400">Разделы</p>
					<ItemButton
						label="Файлы"
						active={activeTab?.type === "upload"}
						onClick={openUploadTab}
						icon={<FolderOpen className="h-4 w-4" />}
					/>
					<ItemButton
						label="Книга расценок"
						active={activeTab?.type === "rates"}
						onClick={openRatesTab}
						icon={<BookOpenText className="h-4 w-4" />}
					/>
					<ItemButton
						label={reconciliationMessages.sidebar_label_verification}
						active={activeTab?.type === "verification"}
						onClick={openVerificationTab}
						icon={<ClipboardCheck className="h-4 w-4" />}
					/>
					<ItemButton
						label={reconciliationMessages.sidebar_label}
						active={
							activeTab?.type === "reconciliation-list" ||
							activeTab?.type === "reconciliation-create" ||
							activeTab?.type === "reconciliation-detail" ||
							activeTab?.type === "reconciliation-result"
						}
						onClick={openReconciliationListTab}
						icon={<GitCompare className="h-4 w-4" />}
					/>
				</div>

				<div className="space-y-1 flex-1">
					<button
						type="button"
						onClick={() => setSettingsOpen((v) => !v)}
						className={`flex w-full items-center justify-between rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
							settingsChildActive
								? "text-blue-700"
								: "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
						}`}
					>
						<span className="inline-flex items-center gap-2">
							<Settings className="h-4 w-4" />
							Настройки
						</span>
						<ChevronDown
							className={`h-3.5 w-3.5 transition-transform ${settingsOpen ? "rotate-180" : ""}`}
						/>
					</button>
					{settingsOpen && (
						<div className="ml-4 space-y-1 border-l border-slate-200 pl-2">
							<ItemButton
								label="Словарь синонимов"
								active={activeTab?.type === "synonyms"}
								onClick={openSynonymsTab}
								icon={<BookType className="h-4 w-4" />}
							/>
							<ItemButton
								label="Логи"
								active={activeTab?.type === "activity-log"}
								onClick={openActivityLogTab}
								icon={<ScrollText className="h-4 w-4" />}
							/>
							<ItemButton
								label="AI-индикатор"
								active={activeTab?.type === "ai-settings"}
								onClick={openAiSettingsTab}
								icon={<Settings className="h-4 w-4" />}
							/>
							{isAdmin && (
								<ItemButton
									label="Пользователи"
									active={activeTab?.type === "users"}
									onClick={openUsersTab}
									icon={<Users className="h-4 w-4" />}
								/>
							)}
						</div>
					)}
				</div>

				<div className="mt-auto space-y-3">
					<AiUsageIndicator />
					<ItemButton
						label="Справка"
						active={activeTab?.type === "help"}
						onClick={openHelpTab}
						icon={<CircleHelp className="h-4 w-4" />}
					/>
					<UserMenu />
				</div>
			</aside>

			<div className="border-b border-slate-200 bg-white p-3 md:hidden">
				<div className="flex items-center gap-2 overflow-x-auto">
					<ItemButton
						label="Файлы"
						active={activeTab?.type === "upload"}
						onClick={openUploadTab}
						icon={<FolderOpen className="h-4 w-4" />}
					/>
					<ItemButton
						label="Расценки"
						active={activeTab?.type === "rates"}
						onClick={openRatesTab}
						icon={<BookOpenText className="h-4 w-4" />}
					/>
					<ItemButton
						label={reconciliationMessages.sidebar_label_verification_mobile}
						active={activeTab?.type === "verification"}
						onClick={openVerificationTab}
						icon={<ClipboardCheck className="h-4 w-4" />}
					/>
					<ItemButton
						label={reconciliationMessages.sidebar_label_mobile}
						active={
							activeTab?.type === "reconciliation-list" ||
							activeTab?.type === "reconciliation-create" ||
							activeTab?.type === "reconciliation-detail" ||
							activeTab?.type === "reconciliation-result"
						}
						onClick={openReconciliationListTab}
						icon={<GitCompare className="h-4 w-4" />}
					/>
					<ItemButton
						label="Синонимы"
						active={activeTab?.type === "synonyms"}
						onClick={openSynonymsTab}
						icon={<BookType className="h-4 w-4" />}
					/>
					<ItemButton
						label="Логи"
						active={activeTab?.type === "activity-log"}
						onClick={openActivityLogTab}
						icon={<ScrollText className="h-4 w-4" />}
					/>
					{isAdmin && (
						<ItemButton
							label="Пользователи"
							active={activeTab?.type === "users"}
							onClick={openUsersTab}
							icon={<Users className="h-4 w-4" />}
						/>
					)}
					<ItemButton
						label="Справка"
						active={activeTab?.type === "help"}
						onClick={openHelpTab}
						icon={<CircleHelp className="h-4 w-4" />}
					/>
				</div>
			</div>
		</>
	);
}
